import React, { useEffect, useState } from 'react';
import styles from './TasksPage.module.scss';
import { AppContainer } from '@/components/AppContainer/AppContainer';
import { BackButton } from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import TasksHeader from './TasksHeader/TasksHeader';
import { SponsorsService } from './services/SponsorsService';
import TaskItem from './TaskItem/TaskItem';
import { Task } from './types';
import { useNotificationStore } from '@/store/NotificationStore';
import { useNavMenuStore } from '@/store/useNavMenuStore';
import { Loader } from '@/components/Loader/Loader';
import TaskPartnerItem from './TaskItem/TaskPartnerItem';
import createAdHandler from 'monetag-tg-sdk';
import BoostModal from '@/components/NormalModal/BoostModal';
import NormalModal from '@/components/NormalModal/NormalModal';
import { useModalStore } from '@/store/NormalModalStore';
import { getLimit } from '@/api/modules/Case/case';
import useLimitStore from '@/store/LimitStore';

const MONETAG_ZONE_ID = 9421495;
const monetagAdHandler = createAdHandler(MONETAG_ZONE_ID);

const TasksPage: React.FC = () => {
  const [boostTasks, setBoostTasks] = useState<any[]>([]);
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { open } = useModalStore();
  const setLimit = useLimitStore((state) => state.setLimit);
  const { setNotification } = useNotificationStore();

  useEffect(() => {
    useNavMenuStore.getState().setVisibility(false);
    return () => {
      useNavMenuStore.getState().setVisibility(true);
    };
  }, []);

  useEffect(() => {
    const fetchLimit = async () => {
      const limit = await getLimit();
      setLimit(limit.data.onclick);
    };
    fetchLimit();
  }, [setLimit]);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setIsLoading(true);
        SponsorsService.setOnTasksUpdate((newTasks) => {
          setTasks(newTasks);
          setIsLoading(false);
          console.log('🔄 TasksPage.fetchTasks - newTasks:', newTasks);
        });
        await SponsorsService.getSponsorTasks();
      } catch (error) {
        console.error('❌ TasksPage.fetchTasks - error:', error);
        setNotification({
          type: 'Error',
          message: 'Ошибка при загрузке заданий',
        });
        setIsLoading(false);
      }
    };
    fetchTasks();
    return () => {
      SponsorsService.setOnTasksUpdate(() => {});
    };
  }, [setNotification]);

  useEffect(() => {
    tasks.forEach((task) => {
      if (task.id === 'monetag-interstitial') {
        monetagAdHandler({ type: 'preload', ymid: String(task.id) }).catch(
          () => {},
        );
      }
    });
  }, [tasks]);

  const handleClaimTask = async (taskId: string | number) => {
    try {
      await SponsorsService.claimSponsorTask(taskId);
      setNotification({
        type: 'Success',
        message: 'Награда успешно получена!',
      });
    } catch (error) {
      console.error('❌ TasksPage.handleClaimTask - error:', error);
      setNotification({
        type: 'Error',
        message: 'Ошибка при получении награды',
      });
    }
  };

  const handleBackClick = () => {
    navigate('/');
  };

  const renderContent = () => {
    const sortedTasks = [...tasks].sort((a, b) => {
      const isA_Banner =
        a.provider === 'BANNER_CPM' || a.provider === 'BANNER_CPC';
      const isB_Banner =
        b.provider === 'BANNER_CPM' || b.provider === 'BANNER_CPC';
      const isA_Done = a.status === 'DONE';
      const isB_Done = b.status === 'DONE';
      const isA_Completed =
        a.status === 'CLAIMED' || a.status === 'IN_PROGRESS';
      const isB_Completed =
        b.status === 'CLAIMED' || b.status === 'IN_PROGRESS';

      if (isA_Banner && !isB_Banner) return -1;
      if (!isA_Banner && isB_Banner) return 1;
      if (isA_Banner && isB_Banner) {
        if (a.provider === 'BANNER_CPM' && b.provider === 'BANNER_CPC')
          return -1;
        if (a.provider === 'BANNER_CPC' && b.provider === 'BANNER_CPM')
          return 1;
      }

      if (isA_Done && !isB_Done) return -1;
      if (!isA_Done && isB_Done) return 1;

      if (isA_Completed && !isB_Completed) return 1;
      if (!isA_Completed && isB_Completed) return -1;

      const isA_Sponsor =
        a.type === 'sponsors' ||
        a.provider === 'FLYER' ||
        a.provider === 'BARZHA' ||
        a.provider === 'SPONSORS';
      const isB_Sponsor =
        b.type === 'sponsors' ||
        b.provider === 'FLYER' ||
        b.provider === 'BARZHA' ||
        b.provider === 'SPONSORS';

      if (isA_Sponsor && !isB_Sponsor) return -1;
      if (!isA_Sponsor && isB_Sponsor) return 1;

      const starsA = a.rewards?.stars || 0;
      const starsB = b.rewards?.stars || 0;
      return starsB - starsA;
    });

    // Условие видимости как раньше: баннеры всегда видны, у остальных должны быть звезды
    const isVisible = (task: Task) =>
      task.provider === 'BANNER_CPM' ||
      task.provider === 'BANNER_CPC' ||
      Boolean(task.rewards?.stars && task.rewards.stars > 0);

    const permanentTasks = sortedTasks.filter(
      (t) =>
        (t.provider === 'BANNER_CPM' || t.provider === 'BANNER_CPC') &&
        isVisible(t),
    );
    const partnerTasks = sortedTasks.filter(
      (t) => t.provider === 'SPONSORS' && isVisible(t),
    );
    const gameTasks = sortedTasks.filter(
      (t) => t.provider === 'GAME_TASKS' && isVisible(t),
    );
    const sponsorTasks = sortedTasks.filter(
      (t) =>
        t.provider !== 'BANNER_CPM' &&
        t.provider !== 'BANNER_CPC' &&
        t.provider !== 'SPONSORS' &&
        t.provider !== 'GAME_TASKS' &&
        isVisible(t),
    );

    const allEmpty =
      permanentTasks.length === 0 &&
      partnerTasks.length === 0 &&
      gameTasks.length === 0 &&
      sponsorTasks.length === 0;

    return (
      <>
        {allEmpty && (
          <div className={styles.tasksPage__empty}>Нет доступных заданий</div>
        )}

        <section className={styles.tasksSection}>
          <h3 className={styles.tasksSection__title}>Постоянные</h3>
          <div className={styles.tasksSection__list}>
            {permanentTasks.map((task) => (
              <TaskItem
                key={`${task.provider}-${task.id}`}
                task={task}
                onClaim={handleClaimTask}
              />
            ))}
          </div>
        </section>

        <section className={styles.tasksSection}>
          <h3 className={styles.tasksSection__title}>Партнерские</h3>
          <div className={styles.tasksSection__list}>
            {partnerTasks.map((task) => (
              <TaskItem
                key={`${task.provider}-${task.id}`}
                task={task}
                onClaim={handleClaimTask}
              />
            ))}
          </div>
        </section>

        <section className={styles.tasksSection}>
          <h3 className={styles.tasksSection__title}>Игровые</h3>
          <div className={styles.tasksSection__list}>
            {gameTasks.map((task) => (
              <TaskItem
                key={`${task.provider}-${task.id}`}
                task={task}
                onClaim={handleClaimTask}
              />
            ))}
          </div>
        </section>

        <section className={styles.tasksSection}>
          <h3 className={styles.tasksSection__title}>Спонсорские</h3>
          <div className={styles.tasksSection__list}>
            {sponsorTasks.map((task) => (
              <TaskItem
                key={`${task.provider}-${task.id}`}
                task={task}
                onClaim={handleClaimTask}
              />
            ))}
          </div>
        </section>

        <TaskPartnerItem />
      </>
    );
  };

  // ⬇️ Подключаем contentSafeAreaInset для CSS
  useEffect(() => {
    const tg = (window as any)?.Telegram?.WebApp;
    if (!tg) return;

    const apply = () => {
      const top = tg?.contentSafeAreaInset?.top ?? 0;
      document.documentElement.style.setProperty(
        '--tg-viewport-content-safe-area-inset-top',
        `${top}px`,
      );
    };

    apply();
    tg.onEvent?.('contentSafeAreaChanged', apply);
    return () => tg.offEvent?.('contentSafeAreaChanged', apply);
  }, []);

  return (
    <div
      className={`${styles.tasksPage} ${isLoading ? styles.tasksPage_loading : ''}`}
    >
      {open === 'boost' ? (
        <NormalModal>
          <BoostModal />
        </NormalModal>
      ) : null}

      <BackButton onClick={handleBackClick} />
      <TasksHeader isLoading={isLoading} />
      {isLoading ? (
        <div className={styles.tasksPage__loaderContainer}>
          <Loader />
        </div>
      ) : (
        <div className={styles.tasksPage__contentContainer}>
          <div className={styles.tasksPage__content}>{renderContent()}</div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
